import * as React from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X } from 'lucide-react';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Opt-in DS styling (matches ChipSelectSearch) — bordered outlined input,
// brand chips, soft popover. Legacy consumers (no `redesign`) are unchanged.
const redesignInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: 14,
    backgroundColor: '#fff',
    padding: '2px 8px',
    '& fieldset': { borderColor: '#e0e1e6' },
    '&:hover fieldset': { borderColor: '#3d5a8a' },
    '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(61, 90, 138, 0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#3d5a8a', borderWidth: '1px' },
  },
  '& .MuiInputBase-input::placeholder': { color: '#546077', opacity: 1 },
};

const redesignChipSx = {
  backgroundColor: '#e8eef7',
  color: '#213d69',
  fontWeight: 500,
  fontSize: 12,
  borderRadius: '4px',
  height: 22,
  '& .MuiChip-label': { paddingLeft: '8px', paddingRight: '4px' },
  '& .MuiChip-deleteIcon': {
    color: '#213d69',
    margin: '0 4px 0 0',
    borderRadius: '2px',
    '&:hover': { color: '#213d69', backgroundColor: 'rgba(61, 90, 138, 0.1)' },
  },
};

const redesignPaperSx = {
  marginTop: '4px',
  border: '1px solid #e0e1e6',
  borderRadius: '8px',
  boxShadow: '0 10px 15px -3px rgba(14, 30, 64, 0.1), 0 4px 6px -4px rgba(14, 30, 64, 0.1)',
  '& .MuiAutocomplete-listbox': {
    '& .MuiAutocomplete-option': { fontSize: 14, color: '#0e1e40' },
  },
};

const ALL = { id: 0, name: 'All' };

export default function MultiSelect({
  options, value, handleChange, label = '', placeholder = '', size = 'small', redesign = false, props,
}) {
  // Redesign behaviour (matches the reference filter): the field is never empty.
  // "All" is the default; picking a real option replaces All, and clearing the
  // last specific selection falls back to All. The selected item is hidden from
  // the dropdown (filterSelectedOptions), so All re-appears once a real option
  // is chosen.
  const handleRedesignChange = (event, newValue) => {
    const justSelectedAll = newValue.some((i) => i.id === 0) && !value.some((i) => i.id === 0);
    if (justSelectedAll || newValue.length === 0) {
      handleChange([ALL]);
      return;
    }
    const reals = newValue.filter((i) => i.id !== 0);
    handleChange(reals.length ? reals : [ALL]);
  };

  const handleLegacyChange = (event, newValue) => {
    const isSelectAll = newValue.some((item) => item.id === 0);
    if (isSelectAll) handleChange([ALL]);
    else handleChange(newValue);
  };

  return (
    <Autocomplete
      multiple
      id="checkboxes-tags-demo"
      options={options}
      disableCloseOnSelect
      disableClearable={redesign}
      filterSelectedOptions={redesign}
      getOptionLabel={(option) => option.name}
      value={value}
      size={size}
      onChange={redesign ? handleRedesignChange : handleLegacyChange}
      getOptionDisabled={redesign ? undefined : (option) => {
        const isSelectedAll = value.some((item) => item.id === 0);
        return (option.id > 0 || option.id !== 0) && isSelectedAll;
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      componentsProps={redesign ? { paper: { sx: redesignPaperSx } } : undefined}
      ChipProps={redesign ? { deleteIcon: <X size={13} />, sx: redesignChipSx } : undefined}
      renderOption={(optProps, option, { selected }) => {
        // eslint-disable-next-line react/prop-types
        const { key, ...optionProps } = optProps;
        if (redesign) {
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <li key={key} {...optionProps}>{option.name}</li>;
        }
        return (
        // eslint-disable-next-line react/jsx-props-no-spreading
          <li key={key} {...optionProps}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth
          sx={redesign ? redesignInputSx : undefined}
        />
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  redesign: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  props: PropTypes.object,
};
